import algoliasearch from 'algoliasearch/lite';

/**
 * Request shape for Algolia `multipleQueries` / InstantSearch multi-index search.
 */
type AlgoliaMultiQueryRequest = Readonly<{
	indexName: string;
	params?: string;
}>;

/**
 * One index slice of a multi-query response (InstantSearch / hits UI).
 * Matches Algolia `Response` loosely — some fields are optional in upstream typings.
 */
type AlgoliaMultiQueryIndexResult = Readonly<{
	hits: unknown[];
	nbHits: number;
	nbPages: number;
	page: number;
	processingTimeMS: number;
	hitsPerPage: number;
	exhaustiveNbHits?: boolean;
	query: string;
	params: string;
}>;

/**
 * Narrow contract for this modal: real `algoliasearch` clients implement this; offline dev uses a stub.
 */
export type ConstellationInstantSearchClient = Readonly<{
	search: (
		requests: readonly AlgoliaMultiQueryRequest[],
	) => Promise<{ results: AlgoliaMultiQueryIndexResult[] }>;
}>;

const EMPTY_ALGOLIA_INDEX_RESULT: AlgoliaMultiQueryIndexResult = {
	hits: [],
	nbHits: 0,
	nbPages: 0,
	page: 0,
	processingTimeMS: 0,
	hitsPerPage: 20,
	exhaustiveNbHits: true,
	query: '',
	params: '',
};

function createOfflineConstellationSearchClient(): ConstellationInstantSearchClient {
	return {
		// NOTE: This is very impartial, a further set of `ReturnType<typeof algoliasearch>` mocking may actually work.
		search(requests: readonly AlgoliaMultiQueryRequest[]) {
			return Promise.resolve({
				results: requests.map(() => ({ ...EMPTY_ALGOLIA_INDEX_RESULT })),
			});
		},
	};
}

function getAlgoliaCredentials() {
	return {
		appId: process.env.GATSBY_CONSTELLATION_ALGOLIA_APP_ID?.trim(),
		searchKey: process.env.GATSBY_CONSTELLATION_ALGOLIA_SEARCH_KEY?.trim(),
	};
}

export function hasAlgoliaCredentials(): string | undefined {
	const { appId, searchKey } = getAlgoliaCredentials();
	return appId && searchKey;
}

/**
 * Create a search client for the Constellation search index that somewhat gracefully handles missing credentials.
 * This does not properly result in searches, but it does not throw an error.
 */
export function createConstellationSearchClient():
	| ReturnType<typeof algoliasearch>
	| ConstellationInstantSearchClient {
	const { appId, searchKey } = getAlgoliaCredentials();
	if (appId && searchKey && hasAlgoliaCredentials()) {
		return algoliasearch(appId, searchKey);
	}

	return createOfflineConstellationSearchClient();
}
